import { createContext, useContext, useState, useEffect } from 'react';
import axios from '../api/axios';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const defaultTheme = {
  primaryColor: '#e8590c',
  primaryHoverColor: '#c94a00',
  secondaryColor: '#64748b',
  backgroundColor: '#f0f2f5',
  sidebarBackground: '#1a1a2e',
  cardBackground: '#ffffff',
  textPrimary: '#1f2937',
  textSecondary: '#6b7280',
  iconColor: '#e8ecf3',
  menuTextColor: '#ffffff',
  menuHoverBg: '#e8590c',
  menuActiveBg: '#e8590c',
  menuActiveText: '#ffffff',
  borderColor: '#e5e7eb',
  successColor: '#10b981',
  warningColor: '#f59e0b',
  errorColor: '#ef4444'
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const fetchTheme = async () => {
    try {
      const response = await axios.get('/company/theme');
      const fetchedTheme = response.data.data;
      if (fetchedTheme && fetchedTheme.primaryColor) {
        // Merge with defaults to ensure all fields are present
        const completeTheme = {
          ...defaultTheme,
          ...fetchedTheme
        };
        setTheme(completeTheme);
      }
    } catch (error) {
      console.error('Failed to fetch theme:', error);
      // Use default theme on error
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (themeData) => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', themeData.primaryColor);
    root.style.setProperty('--primary-hover-color', themeData.primaryHoverColor);
    root.style.setProperty('--secondary-color', themeData.secondaryColor);
    root.style.setProperty('--background-color', themeData.backgroundColor);
    root.style.setProperty('--sidebar-background', themeData.sidebarBackground);
    root.style.setProperty('--card-background', themeData.cardBackground);
    root.style.setProperty('--text-primary', themeData.textPrimary);
    root.style.setProperty('--text-secondary', themeData.textSecondary);
    root.style.setProperty('--icon-color', themeData.iconColor);
    root.style.setProperty('--menu-text-color', themeData.menuTextColor);
    root.style.setProperty('--menu-hover-bg', themeData.menuHoverBg);
    root.style.setProperty('--menu-active-bg', themeData.menuActiveBg);
    root.style.setProperty('--menu-active-text', themeData.menuActiveText);
    root.style.setProperty('--border-color', themeData.borderColor);
    root.style.setProperty('--success-color', themeData.successColor);
    root.style.setProperty('--warning-color', themeData.warningColor);
    root.style.setProperty('--error-color', themeData.errorColor);
  };

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  const value = {
    theme,
    updateTheme,
    resetTheme,
    loading,
    defaultTheme
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
